import type {
  DeviceReadiness,
  ReadinessCheck,
  ReadinessState,
  Requirement,
} from "@trip/contracts/bridge";
import {
  BatteryCharging,
  Bell,
  Check,
  HelpCircle,
  Images,
  MapPin,
  Power,
  RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { Banner, Card, ICON } from "../ui/index.ts";

/**
 * What this phone has to allow before a trip's route records itself unattended.
 *
 * The route is what places media on the map, so a stretch the phone did not record is a set of
 * photos with no honest position. Most of what stops the recording is not in the app's hands: the
 * system's own power saving, and on some phones a manufacturer switch that decides whether an app
 * may come back at all after a restart.
 *
 * Two kinds of row, and the difference is deliberate. A row the system will answer for is read and
 * asked for properly. A row it will not answer — the manufacturer's auto-start switch has no API to
 * read or open, on ColorOS it is behind a signature permission — can only be explained, and is
 * never drawn as done: the most it can say is that the person said they did it.
 */
const COPY: Record<Requirement, { title: string; why: string; action: string; icon: typeof Bell }> =
  {
    notifications: {
      title: "Show a recording notice",
      why: "While your route is being recorded, the app keeps a notice on screen so you always know it is on.",
      action: "Allow notifications",
      icon: Bell,
    },
    location: {
      title: "Use your location",
      why: "Your route is what places your photos and videos on the trip map.",
      action: "Allow location",
      icon: MapPin,
    },
    locationAlways: {
      title: "Keep recording with the app closed",
      why: "Choose “Allow all the time”. With “only while using the app”, your route stops the moment you leave the app, and anything you record then can’t be placed on the map.",
      action: "Allow all the time",
      icon: MapPin,
    },
    batteryOptimisation: {
      title: "Don’t put the app to sleep",
      why: "Battery saving deep-sleeps apps once the phone has been idle a while, which on a trip is most of every night.",
      action: "Turn off battery saving for this app",
      icon: BatteryCharging,
    },
    backgroundUnrestricted: {
      title: "Let the app run in the background",
      why: "Some phones restrict an app in the background, which stops it recording when you are not looking at it.",
      action: "Open settings",
      icon: RefreshCw,
    },
    media: {
      title: "Read your photos and videos",
      why: "The trip collects footage from this phone, so it has to be able to read the camera roll.",
      action: "Allow photos and videos",
      icon: Images,
    },
    oemAutoStart: {
      title: "Let the app start on its own",
      why: "Your phone’s manufacturer decides whether an app may start again after a restart. If it may not, your route stops at the next reboot and never comes back. Look for “Auto-launch”, “Auto-start” or “Allow background running” in your phone’s battery or app settings.",
      action: "I’ve turned this on",
      icon: Power,
    },
  };

/** Only a state the system itself confirmed may be drawn as settled. */
const isDone = (state: ReadinessState) => state === "OK";

/** What the one button on a row should do, or null when the row has nothing to offer. */
function actionFor(
  state: ReadinessState,
): "request" | "openSettings" | "acknowledge" | "forget" | null {
  if (state === "MISSING") return "request";
  if (state === "BLOCKED") return "openSettings";
  if (state === "UNVERIFIABLE") return "acknowledge";
  // Asking again only re-opens Android's picker, so full access is a trip to Settings.
  if (state === "PARTIAL") return "openSettings";
  // Nothing measured this, so the person must be able to withdraw what they said about it.
  if (state === "ACKNOWLEDGED") return "forget";
  return null;
}

/** What a state needs said about it beyond the row's own reason, or nothing. */
function note(state: ReadinessState): string | null {
  if (state === "PARTIAL") {
    return "Your phone is sharing only the photos and videos you picked, so everything else from the trip would be left behind.";
  }
  if (state === "ACKNOWLEDGED") {
    return "We can’t check this one — your phone doesn’t let any app read it, so this is your word for it.";
  }
  if (state === "UNVERIFIABLE") {
    return "We can’t check this one — your phone doesn’t let any app read it.";
  }
  return null;
}

function Row({
  check,
  busy,
  onAct,
}: {
  check: ReadinessCheck;
  busy: boolean;
  onAct: (method: "request" | "openSettings" | "acknowledge" | "forget") => void;
}) {
  const copy = COPY[check.id];
  const Icon = copy.icon;
  const action = actionFor(check.state);
  const unreadable = check.state === "UNVERIFIABLE" || check.state === "ACKNOWLEDGED";
  const said = note(check.state);
  return (
    <div className="setup-row">
      <span className="setup-mark">
        {isDone(check.state) ? (
          <Check {...ICON} aria-label="Allowed" />
        ) : unreadable ? (
          <HelpCircle {...ICON} aria-hidden="true" />
        ) : (
          <Icon {...ICON} aria-hidden="true" />
        )}
      </span>
      <span className="setup-text">
        <strong>{copy.title}</strong>
        <span className="small">{copy.why}</span>
        {said !== null && <span className="small muted">{said}</span>}
      </span>
      {action && (
        <button
          type="button"
          className="button small"
          disabled={busy}
          onClick={() => onAct(action)}
        >
          {action === "openSettings"
            ? "Open settings"
            : action === "forget"
              ? "Check again"
              : copy.action}
        </button>
      )}
    </div>
  );
}

export function PhoneSetupPage({
  bridge = nativeBridge(),
  onDone,
}: {
  bridge?: Bridge | null;
  onDone: () => void;
}) {
  const [readiness, setReadiness] = useState<DeviceReadiness | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bridge) return;
    let gone = false;
    void bridge
      .call("device.readiness", {})
      .then((r) => {
        if (!gone) setReadiness(r);
      })
      .catch(() => {
        if (!gone) setError("We couldn’t read this phone’s settings.");
      });
    const off = bridge.on("device.readiness", setReadiness);
    return () => {
      gone = true;
      void off();
    };
  }, [bridge]);

  if (!bridge) return null;

  async function act(
    requirement: Requirement,
    method: "request" | "openSettings" | "acknowledge" | "forget",
  ) {
    if (!bridge) return;
    setBusy(true);
    setError(null);
    try {
      setReadiness(await bridge.call(`device.${method}`, { requirement }));
    } catch {
      setError("That didn’t go through. You can also change it in your phone’s settings.");
    } finally {
      setBusy(false);
    }
  }

  const checks = readiness?.checks ?? [];
  return (
    <section className="stack">
      <h1>Before your first trip</h1>
      <p className="small">
        Your route is what puts your photos and videos in the right place on the map. For it to keep
        recording while you are busy travelling, this phone has to allow a few things.
      </p>
      {error && <Banner tone="bad" message={error} />}
      <Card title="This phone">
        {readiness === null ? (
          <p className="muted">Checking…</p>
        ) : (
          checks.map((check) => (
            <Row
              key={check.id}
              check={check}
              busy={busy}
              onAct={(method) => void act(check.id, method)}
            />
          ))
        )}
      </Card>
      <div className="setup-done">
        <button type="button" className="button primary" onClick={onDone}>
          {readiness?.complete ? "Done" : "Not now"}
        </button>
        {readiness !== null && !readiness.complete && (
          <span className="small muted">You can finish this later from Settings.</span>
        )}
      </div>
    </section>
  );
}

/** Remembered per device, not per account: it is this phone's settings that were explained. */
const SEEN = "phone-setup-seen";

function seen(): boolean {
  try {
    return localStorage.getItem(SEEN) === "1";
  } catch {
    // A private window, or site data blocked. Better to ask again than to strand someone.
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(SEEN, "1");
  } catch {
    /* nothing to do: the setup is still reachable from Settings */
  }
}

/**
 * Shows phone setup once, on the first run inside the app, and then gets out of the way.
 *
 * A browser never sees it: there is no bridge there, and nothing about a phone's power settings
 * applies. Neither does anyone whose phone is already set up, or who has been asked once before —
 * nothing here is a wall, because Android stops offering a permission that has been refused twice
 * and a gate nobody can pass would be worse than a trip that records less.
 */
export function FirstRunGate({
  bridge = nativeBridge(),
  children,
}: {
  bridge?: Bridge | null;
  children: React.ReactNode;
}) {
  const [ask, setAsk] = useState<boolean | null>(bridge && !seen() ? null : false);

  useEffect(() => {
    if (!bridge || seen()) return;
    let gone = false;
    void bridge
      .call("device.readiness", {})
      .then((r) => {
        if (!gone) setAsk(!r.complete);
      })
      // Unreadable for any reason: fall through to the app rather than hold it up.
      .catch(() => {
        if (!gone) setAsk(false);
      });
    return () => {
      gone = true;
    };
  }, [bridge]);

  if (ask === null) return null;
  if (!ask) return <>{children}</>;
  return (
    <PhoneSetupPage
      bridge={bridge}
      onDone={() => {
        remember();
        setAsk(false);
      }}
    />
  );
}

/** The way back into phone setup, for Settings. Nothing at all in a browser. */
export function PhoneSetupEntry({ bridge = nativeBridge() }: { bridge?: Bridge | null }) {
  const [open, setOpen] = useState(false);
  if (!bridge) return null;
  if (open) return <PhoneSetupPage bridge={bridge} onDone={() => setOpen(false)} />;
  return (
    <Card title="This phone">
      <p className="muted small" style={{ margin: 0 }}>
        What this phone has to allow for your route to keep recording while you travel.
      </p>
      <div className="row wrap">
        <button type="button" className="button" onClick={() => setOpen(true)}>
          Phone setup
        </button>
      </div>
    </Card>
  );
}
